import { PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { InternalError } from '@systemeio/util'
import { SettingsSection } from 'modules/profile/components/settings-section'
import React, { useMemo, useRef, useState } from 'react'
import { EditDataType, EditProps, TempStateType } from 'shared/components/edit-modal'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface StoreEditColumnsProps<T, K>
  extends Omit<
    EditProps<T, K>,
    | 'opened'
    | 'onClose'
    | 'afterLeave'
    | 'caption'
    | 'isPreFetching'
    | 'onOpen'
    | 'fullCaption'
    | 'toastCaption'
    | 'additionalInfo'
    | 'additionalData'
  > {}

const StoreEditForm = <T, K>({ instance, editable, onEdit }: StoreEditColumnsProps<T, K>) => {
  const { t } = useLocoTranslation()
  const [error, setError] = useState('')

  const editableRef = useRef(editable)

  const defaultValues = useMemo((): TempStateType<T> => {
    const obj: TempStateType<T> = {} as TempStateType<T>
    editableRef.current.forEach(value => {
      if (Array.isArray(value)) {
        value.forEach(el => {
          obj[el.field] = instance[el.field]
        })
      } else {
        obj[value.field] = instance[value.field]
      }
    })
    return obj
  }, [instance])

  const [tempState, setTempState] = useState<TempStateType<T>>(defaultValues)

  const disabledSave = editable.some(setting => {
    if (Array.isArray(setting)) {
      return setting.some(item => (item.disabled ? item.disabled(tempState[item.field]) : false))
    } else {
      return setting.disabled ? setting.disabled(tempState[setting.field]) : false
    }
  })

  const [isFetching, setIsFetching] = useState(false)

  const handleConfirm = async () => {
    try {
      setError('')
      setIsFetching(true)
      await onEdit(tempState as EditDataType<T>)
      setIsFetching(false)
      toast.success(t('dashboard.actions.updated', { module: t('dashboard.store.title') }))
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''))
        }
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  return (
    <SettingsSection
      className="bg-background-secondary rounded-sm p-6"
      headingTitle={t('dashboard.store.base_settings.title')}
    >
      <form
        className="flex flex-col gap-6 lg:gap-10"
        onSubmit={async (e: React.FormEvent) => {
          e.preventDefault()
          await handleConfirm()
        }}
      >
        <div className="flex flex-col gap-4">
          {editable.map((setting, idx) => {
            if (Array.isArray(setting)) {
              return (
                <div
                  key={`${idx}-wrapper`}
                  className="[&>*]:flex-1 flex items-start justify-between gap-10"
                >
                  {setting.map(
                    (el, elIdx) =>
                      (el.shouldRender
                        ? el.shouldRender(tempState as EditDataType<T>, instance)
                        : true) && (
                        <React.Fragment key={`${idx}-${String(el.field || elIdx)}`}>
                          {el.onRender(
                            tempState[el.field],
                            value => setTempState(prev => ({ ...prev, [el.field]: value })),
                            tempState,
                            instance,
                            false,
                            newState => setTempState(newState),
                          )}
                        </React.Fragment>
                      ),
                  )}
                </div>
              )
            } else {
              return (
                (setting.shouldRender
                  ? setting.shouldRender(tempState as EditDataType<T>, instance)
                  : true) && (
                  <React.Fragment key={`${idx}-${String(setting.field)}`}>
                    {setting.onRender(
                      tempState[setting.field],
                      value => setTempState(prev => ({ ...prev, [setting.field]: value })),
                      tempState,
                      instance,
                      false,
                      newState => setTempState(newState),
                    )}
                  </React.Fragment>
                )
              )
            }
          })}
        </div>
        <div className="flex flex-col justify-center gap-2">
          <PrimaryButton
            className="self-center"
            disabled={disabledSave}
            width="large"
            isFetching={isFetching}
            type="submit"
          >
            {t('global.save')}
          </PrimaryButton>

          <FieldErrorAndDescription error={error} errorClassName={'text-center'} />
        </div>
      </form>
    </SettingsSection>
  )
}

export default StoreEditForm
